import { apiService } from './api';

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: any;
  store?: any;
  authType?: 'oauth' | 'embedded' | 'embedded_with_oauth';
  needsOAuth?: boolean;
  oauthUrl?: string;
  message?: string;
  error?: string;
}

export interface TokenValidationResponse {
  valid: boolean;
  user?: any;
  exp?: number;
  error?: string;
}

class AuthService {
  private readonly API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app.translationflow.microdesk.space';

  /**
   * Main authentication method - handles both embedded and OAuth flows
   */
  async authenticate(shop: string, authType: 'oauth' | 'embedded' = 'oauth', embeddedParams?: any): Promise<AuthResponse> {
    try {
      console.log(`Authenticating shop: ${shop}, authType: ${authType}`);
      
      const requestBody = {
        shop,
        authType,
        embeddedParams
      };
      
      console.log('Authentication request body:', requestBody);

      const response = await fetch(`${this.API_BASE_URL}/api/auth/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Authentication response status:', response.status);
      
      const data = await response.json();
      console.log('Authentication response data:', data);
      
      return data;
    } catch (error) {
      console.error('Authentication error:', error);
      return {
        success: false,
        error: 'Authentication failed'
      };
    }
  }

  /**
   * Validate JWT token
   */
  async validateToken(token: string): Promise<TokenValidationResponse> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        valid: false,
        error: 'Token validation failed'
      };
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<any> {
    try {
      const response = await apiService.getCurrentUser();
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Store authentication data in localStorage
   */
  storeAuthData(token: string, user: any, store: any): void {
    localStorage.setItem('authToken', token);
    localStorage.setItem('auth_token', token);
    localStorage.setItem('userData', JSON.stringify(user));
    localStorage.setItem('storeData', JSON.stringify(store));
  }

  /**
   * Clear authentication data from localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('userData');
    localStorage.removeItem('storeData');
    localStorage.removeItem('shopify_oauth_shop');
    localStorage.removeItem('shopify_oauth_host');
    localStorage.removeItem('shopify_oauth_id_token');
    localStorage.removeItem('shopify_oauth_hmac');
    localStorage.removeItem('shopify_oauth_session');
    localStorage.removeItem('shopify_oauth_timestamp');
  }

  /**
   * Get stored authentication data
   */
  getStoredAuthData(): { token: string | null; user: any; store: any } {
    const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
    const userData = localStorage.getItem('userData');
    const storeData = localStorage.getItem('storeData');

    return {
      token,
      user: userData ? JSON.parse(userData) : null,
      store: storeData ? JSON.parse(storeData) : null
    };
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const { token } = this.getStoredAuthData();
    return !!token;
  }

  /**
   * Handle OAuth flow
   */
  async handleOAuthFlow(shop: string): Promise<AuthResponse> {
    return this.authenticate(shop, 'oauth');
  }

  /**
   * Handle embedded authentication
   */
  async handleEmbeddedAuth(shop: string, embeddedParams: any): Promise<AuthResponse> {
    return this.authenticate(shop, 'embedded', embeddedParams);
  }

  /**
   * Extract shop from URL
   */
  extractShopFromUrl(): string | null {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    let shopParam = urlParams.get('shop');
    console.log('URL params:', window.location.search);
    console.log('Shop param:', shopParam);
    
    // If no shop param in URL, check sessionStorage for preserved parameters
    if (!shopParam) {
      const preservedParams = sessionStorage.getItem('login_url_params');
      console.log('Checking preserved params:', preservedParams);
      if (preservedParams) {
        const preservedUrlParams = new URLSearchParams(preservedParams);
        shopParam = preservedUrlParams.get('shop');
        console.log('Preserved params:', preservedParams);
        console.log('Preserved shop param:', shopParam);
      } else {
        console.log('No preserved params found in sessionStorage');
      }
    }
    
    if (shopParam) {
      return shopParam;
    }

    // Check URL path for shop pattern
    const pathMatch = window.location.pathname.match(/\/store\/([^.]+)\.myshopify\.com/);
    if (pathMatch) {
      return pathMatch[1] + '.myshopify.com';
    }

    // Check if we're in Shopify admin context
    if (window.location.href.includes('admin.shopify.com')) {
      const urlMatch = window.location.href.match(/([^.]+)\.myshopify\.com/);
      if (urlMatch) {
        return urlMatch[1] + '.myshopify.com';
      }
    }

    // Check referrer for shop pattern
    if (document.referrer && document.referrer.includes('admin.shopify.com')) {
      const referrerMatch = document.referrer.match(/([^.]+)\.myshopify\.com/);
      if (referrerMatch) {
        return referrerMatch[1] + '.myshopify.com';
      }
    }

    return null;
  }

  /**
   * Check if we're in Shopify admin context
   */
  isInShopifyAdmin(): boolean {
    return window.location.href.includes('admin.shopify.com') ||
           document.referrer.includes('admin.shopify.com') ||
           window.location.href.includes('myshopify.com');
  }

  /**
   * Get embedded parameters from URL
   */
  getEmbeddedParams(): any {
    let urlParams = new URLSearchParams(window.location.search);
    
    console.log('=== GET EMBEDDED PARAMS ===');
    console.log('Current URL:', window.location.href);
    console.log('URL search:', window.location.search);
    console.log('URL params:', urlParams.toString());
    
    // If no parameters in URL, check sessionStorage for preserved parameters
    if (!urlParams.toString()) {
      const preservedParams = sessionStorage.getItem('login_url_params');
      console.log('No URL params, checking sessionStorage:', preservedParams);
      if (preservedParams) {
        urlParams = new URLSearchParams(preservedParams);
        console.log('Using preserved params:', preservedParams);
      }
    }
    
    const params = {
      id_token: urlParams.get('id_token'),
      hmac: urlParams.get('hmac'),
      session: urlParams.get('session'),
      host: urlParams.get('host'),
      shop: urlParams.get('shop'),
      timestamp: urlParams.get('timestamp')
    };
    
    console.log('Extracted embedded params:', params);
    return params;
  }

  /**
   * Check if we have embedded parameters
   */
  hasEmbeddedParams(): boolean {
    const params = this.getEmbeddedParams();
    return !!(params.id_token && params.hmac && params.session);
  }

  /**
   * Check if we have OAuth callback parameters
   */
  hasOAuthCallbackParams(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(urlParams.get('code') && urlParams.get('hmac') && urlParams.get('state'));
  }
}

export const authService = new AuthService(); 